// core/DomHelpers.ts
/** Универсальные помощники для работы с DOM */
class DomHelpers {
    /**
    * Прячет элемент(ы) (аналог jQuery.hide) с запоминанием inline-display.
    * Если указан `options.duration > 0`, плавно анимирует прозрачность до 0, затем скрывает.
    *
    * @param els Один элемент или коллекция элементов
    * @param options.duration Длительность анимации в мс (по умолчанию мгновенно)
    */
    static hide(els, options = {}) {
        const { duration = 0 } = options;
        for (const el of DomHelpers._asIterable(els)) {
            // запомним inline-display, если ещё не прятали
            if (!el.dataset.prevDisplay) {
                const inline = el.style.display;
                if (inline && inline !== "none") {
                    el.dataset.prevDisplay = inline;
                }
            }
            if (duration > 0) {
                // если уже скрыт — ничего не делаем
                if (el.style.display === "none" || el.hidden) {
                    continue;
                }
                // Подготовка к анимации
                const prevTransition = el.style.transition;
                el.dataset.prevTransition = prevTransition || "";
                // гарантируем начальную прозрачность 1
                const prevOpacity = el.style.opacity;
                el.dataset.prevOpacity = prevOpacity || "";
                // Ставим анимацию прозрачности
                el.style.transition = `opacity ${duration}ms ease`;
                el.style.opacity = "1";
                // Следующим кадром гасим
                requestAnimationFrame(() => {
                    el.style.opacity = "0";
                    const onEnd = (ev) => {
                        if (ev.propertyName !== "opacity")
                            return;
                        el.removeEventListener("transitionend", onEnd);
                        // Итоги: реально скрываем
                        el.hidden = true;
                        el.style.display = "none";
                        // Чистим временные стили и восстанавливаем предыдущие
                        DomHelpers._restoreStyle(el, ["opacity", "transition"]);
                    };
                    el.addEventListener("transitionend", onEnd);
                });
            }
            else {
                // мгновенное скрытие
                el.hidden = true;
                el.style.display = "none";
            }
        }
    }
    /**
     * Показывает элемент(ы) (аналог jQuery.show) с восстановлением inline-display.
     * Если указан `options.duration > 0`, плавно анимирует прозрачность от 0 до 1.
     *
     * @param els Один элемент или коллекция элементов
     * @param display Принудительный display (если нужен конкретный)
     * @param options.duration Длительность анимации в мс (по умолчанию мгновенно)
     */
    static show(els, options = {}) {
        const { duration = 0 } = options;
        for (const el of DomHelpers._asIterable(els)) {
            // восстановим display
            el.hidden = false;
            if (options.display) {
                el.style.display = options.display;
            }
            else if (el.dataset.prevDisplay) {
                el.style.display = el.dataset.prevDisplay;
            }
            else {
                el.style.removeProperty("display");
            }
            delete el.dataset.prevDisplay;
            if (duration > 0) {
                // Подготовка к анимации: из 0 → 1
                const prevTransition = el.style.transition;
                el.dataset.prevTransition = prevTransition || "";
                const prevOpacity = el.style.opacity;
                el.dataset.prevOpacity = prevOpacity || "";
                // Сначала делаем прозрачным (видимый, но alpha 0)
                el.style.opacity = "0";
                // Форсим reflow, чтобы браузер заметил 0
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                el.offsetWidth;
                // Включаем анимацию и поднимаем до 1
                el.style.transition = `opacity ${duration}ms ease`;
                requestAnimationFrame(() => {
                    el.style.opacity = "1";
                    const onEnd = (ev) => {
                        if (ev.propertyName !== "opacity")
                            return;
                        el.removeEventListener("transitionend", onEnd);
                        // Чистим временные стили и восстанавливаем предыдущие
                        DomHelpers._restoreStyle(el, ["opacity", "transition"]);
                    };
                    el.addEventListener("transitionend", onEnd);
                });
            }
        }
    }
    /**
     * Переключает видимость: если скрыт → show, иначе → hide.
     * @param els Элемент(ы)
     * @param display Принудительный display (если нужен)
     * @param options Опции анимации
     */
    static toggle(els, options = {}) {
        for (const el of this._asIterable(els)) {
            if (this.isHidden(el)) {
                this.show(el, options);
            }
            else {
                this.hide(el, options);
            }
        }
    }
    /**
     * Переключает элемент между slideUp и slideDown (аналог jQuery.slideToggle).
     * @param el Элемент
     * @param duration Длительность анимации в мс (по умолчанию 300)
     */
    static slideToggle(el, duration = 300) {
        if (this.isHidden(el)) {
            this.slideDown(el, duration);
        }
        else {
            this.slideUp(el, duration);
        }
    }
    /**
     * Возвращает max-height элемента в px, либо Infinity если ограничения нет.
     */
    static _getMaxHeightPx(el) {
        const maxH = getComputedStyle(el).maxHeight;
        if (!maxH || maxH === "none")
            return Number.POSITIVE_INFINITY;
        const px = Number.parseFloat(maxH);
        return Number.isFinite(px) ? px : Number.POSITIVE_INFINITY;
    }
    /**
     * Плавно сворачивает элемент (аналог jQuery.slideUp).
     * @param el Элемент
     * @param duration Длительность анимации в мс (по умолчанию 300)
     */
    static slideUp(el, duration = 300) {
        if (this.isHidden(el))
            return;
        // отменяем предыдущее
        this._cancelSlide(el);
        // Берём текущую видимую высоту, а не scrollHeight  // <-- FIX
        const startHeight = Math.round(el.getBoundingClientRect().height);
        const startTime = performance.now();
        el.style.overflow = "hidden";
        el.style.height = startHeight + "px";
        const animate = (time) => {
            const progress = Math.min((time - startTime) / duration, 1);
            el.style.height = startHeight * (1 - progress) + "px";
            if (progress < 1) {
                const id = requestAnimationFrame(animate);
                this._slideAnimations.set(el, id);
            }
            else {
                el.style.display = "none";
                el.style.removeProperty("height");
                el.style.removeProperty("overflow");
                el.hidden = true;
                this._slideAnimations.delete(el);
            }
        };
        const id = requestAnimationFrame(animate);
        this._slideAnimations.set(el, id);
    }
    /**
    * Плавно разворачивает элемент (аналог jQuery.slideDown).
    * @param el Элемент
    * @param duration Длительность анимации в мс (по умолчанию 300)
    */
    static slideDown(el, duration = 300) {
        if (!this.isHidden(el))
            return;
        // отменяем предыдущее
        this._cancelSlide(el);
        el.hidden = false;
        el.style.removeProperty("display");
        if (getComputedStyle(el).display === "none") {
            el.style.display = "block";
        }
        const maxHeightPx = this._getMaxHeightPx(el);
        const targetHeight = Math.min(el.scrollHeight, maxHeightPx); // <-- FIX
        const startTime = performance.now();
        el.style.height = "0px";
        el.style.overflow = "hidden";
        const animate = (time) => {
            const progress = Math.min((time - startTime) / duration, 1);
            el.style.height = targetHeight * progress + "px";
            if (progress < 1) {
                const id = requestAnimationFrame(animate);
                this._slideAnimations.set(el, id);
            }
            else {
                el.style.removeProperty("height");
                el.style.removeProperty("overflow");
                this._slideAnimations.delete(el);
            }
        };
        const id = requestAnimationFrame(animate);
        this._slideAnimations.set(el, id);
    }
    /** Вспомогательное: отмена активной анимации */
    static _cancelSlide(el) {
        const prevId = this._slideAnimations.get(el);
        if (prevId != null) {
            cancelAnimationFrame(prevId);
            this._slideAnimations.delete(el);
        }
    }
    /** Добавляет CSS-классы к элементу или коллекции */
    static addClass(els, ...classNames) {
        for (const el of this._asIterable(els)) {
            el.classList.add(...classNames);
        }
    }
    /** Удаляет CSS-классы с элемента или коллекции */
    static removeClass(els, ...classNames) {
        for (const el of this._asIterable(els)) {
            el.classList.remove(...classNames);
        }
    }
    /** Устанавливает CSS-классы и убирает их после задержки */
    static addTmpClass(els, delay = 1600, ...classNames) {
        this.addClass(els, ...classNames);
        setTimeout(() => {
            this.removeClass(els, ...classNames);
        }, isNaN(delay) ? 1600 : delay);
    }
    /** Восстановление временно изменённых inline-стилей (opacity/transition) */
    static _restoreStyle(el, props) {
        for (const p of props) {
            const key = p === "opacity" ? "prevOpacity" : "prevTransition";
            const saved = el.dataset[key];
            if (saved !== undefined) {
                if (saved === "")
                    el.style.removeProperty(p);
                else
                    el.style[p] = saved;
                delete el.dataset[key];
            }
            else {
                el.style.removeProperty(p);
            }
        }
    }
    /** Внутренний хелпер: нормализует вход в Iterable<HTMLElement> */
    static _asIterable(els) {
        return (els instanceof HTMLElement) ? [els] : els;
    }
    /** Проверка, скрыт ли элемент */
    static isHidden(el) {
        const cs = getComputedStyle(el);
        return cs.display === "none" || cs.visibility === "hidden";
    }
    /** jQuery-подобная «видимость»: элемент есть в DOM, не display:none / visibility:hidden и имеет прямоугольники */
    static isVisible(el) {
        if (!el.isConnected)
            return false;
        const cs = getComputedStyle(el);
        if (cs.display === "none" || cs.visibility === "hidden" || el.hidden)
            return false;
        return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
    }
    /**
     * Ищет ближайший scrollable-родитель.
     */
    static closestScrollParent(el) {
        let parent = el.parentElement;
        while (parent) {
            const style = getComputedStyle(parent);
            if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
                return parent;
            }
            parent = parent.parentElement;
        }
        return null;
    }
    /**
     * Проверяет, видим ли элемент в пределах viewport.
     * @param el Элемент
     * @param partial true — достаточно частичной видимости
     * @param hidden учитывать ли элементы с нулевой площадью
     * @param direction "both" | "vertical" | "horizontal"
     */
    static isInViewport(el, partial = false, hidden = false, direction = "both") {
        // 1) Базовая «отрисованность»
        if (!DomHelpers.isVisible(el))
            return false;
        // Исходный прямоугольник элемента
        const elRect = el.getBoundingClientRect();
        // 2) Клиппинг предками
        let clipRect = new DOMRect(elRect.left, elRect.top, elRect.width, elRect.height);
        let wasClipped = false;
        for (let p = el.parentElement; p; p = p.parentElement) {
            const cs = getComputedStyle(p);
            const clips = cs.overflow !== "visible" ||
                cs.overflowX !== "visible" ||
                cs.overflowY !== "visible";
            if (!clips)
                continue;
            const pr = p.getBoundingClientRect();
            const left = Math.max(clipRect.left, pr.left);
            const top = Math.max(clipRect.top, pr.top);
            const right = Math.min(clipRect.right, pr.right);
            const bottom = Math.min(clipRect.bottom, pr.bottom);
            if (right <= left || bottom <= top) {
                return false; // полностью обрезан
            }
            wasClipped ||= (left > clipRect.left) ||
                (top > clipRect.top) ||
                (right < clipRect.right) ||
                (bottom < clipRect.bottom);
            clipRect = new DOMRect(left, top, right - left, bottom - top);
        }
        // 3) Сравнение с окном (вьюпортом)
        const vpW = document.documentElement.clientWidth;
        const vpH = document.documentElement.clientHeight;
        // Пересечение видимой части с вьюпортом
        const vLeft = Math.max(clipRect.left, 0);
        const vTop = Math.max(clipRect.top, 0);
        const vRight = Math.min(clipRect.right, vpW);
        const vBottom = Math.min(clipRect.bottom, vpH);
        const vOverlap = Math.max(0, vBottom - vTop);
        const hOverlap = Math.max(0, vRight - vLeft);
        const zeroW = elRect.width === 0;
        const zeroH = elRect.height === 0;
        // Требование к ненулевой площади, если hidden === true
        const hasClientSize = hidden ? (el.offsetWidth * el.offsetHeight) > 0 : true;
        // Для partial:
        //  - если hidden === false и размер по оси == 0 — ось автоматически считается видимой
        //  - иначе требуем положительный оверлап по оси
        const vPartialVisible = zeroH && !hidden ? true : (vOverlap > 0);
        const hPartialVisible = zeroW && !hidden ? true : (hOverlap > 0);
        // Для full:
        //  - нельзя чтобы был клиппинг предками или вьюпортом
        //  - весь elRect должен помещаться во вьюпорт целиком
        const fitsViewportFull = elRect.left >= 0 && elRect.top >= 0 &&
            elRect.right <= vpW && elRect.bottom <= vpH;
        const vFullVisible = zeroH && !hidden ? true : fitsViewportFull;
        const hFullVisible = zeroW && !hidden ? true : fitsViewportFull;
        const vVisible = partial ? vPartialVisible : vFullVisible;
        const hVisible = partial ? hPartialVisible : hFullVisible;
        // Если проверяем full, запрещаем любой клиппинг предками/вьюпортом
        const noClippingForFull = partial ? true : !wasClipped;
        if (!hasClientSize)
            return false;
        if (direction === "vertical")
            return vVisible && (partial || noClippingForFull);
        if (direction === "horizontal")
            return hVisible && (partial || noClippingForFull);
        return vVisible && hVisible && (partial || noClippingForFull);
    }
    /** Проверяет, есть ли горизонтальный скролл у элемента */
    static isHScrollable(el) {
        return el.scrollWidth > el.clientWidth;
    }
    /** Проверяет, есть ли вертикальный скролл у элемента */
    static isVScrollable(el) {
        return el.scrollHeight > el.clientHeight;
    }
    /** Проверяет, есть ли скролл у элемента (по горизонтали или вертикали) */
    static isScrollable(el) {
        return el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight;
    }
    /**
     * Прокручивает страницу или любой контейнер к элементу/позиции.
     *
     * @param container Контейнер для скролла (HTMLElement, window или селектор-строка).
     * @param target    Селектор, DOM-элемент или {top, left}
     * @param options   duration: длительность, axis: направления, offset, onDone, onStart, onCancel
     * @returns         { cancel: () => void }
     */
    static scrollTo(container, target, options = {}) {
        let cancelled = false;
        if (!target)
            return { cancel: () => { } };
        let el = null;
        // Явно: если target - строка или HTMLElement
        if (typeof target === 'string') {
            el = document.querySelector(target);
        }
        else if (target instanceof HTMLElement) {
            el = target;
        }
        // Подбор контейнера
        const resolveContainer = () => {
            // 1) Явно заданный контейнер
            if (container && container !== "auto") {
                if (typeof container === "string") {
                    const c = document.querySelector(container);
                    if (!c) {
                        console.warn(`Container not found: ${container}, fallback to window`);
                        return window;
                    }
                    return c;
                }
                return container; // HTMLElement | Window
            }
            // 2) Автоподбор (container == null/undefined/"auto")
            if (el) {
                const sp = DomHelpers.closestScrollParent(el);
                if (sp)
                    return sp;
            }
            return window;
        };
        const resolvedContainer = resolveContainer();
        console.log('resolvedContainer', resolvedContainer);
        // === КООРДИНАТЫ ===
        let top = 0, left = 0;
        if (el) {
            const rect = el.getBoundingClientRect();
            if (resolvedContainer === window) {
                top = rect.top + window.pageYOffset;
                left = rect.left + window.pageXOffset;
            }
            else {
                const cont = resolvedContainer;
                const contRect = cont.getBoundingClientRect();
                top = rect.top - contRect.top + cont.scrollTop;
                left = rect.left - contRect.left + cont.scrollLeft;
                // --- Коррекция на sticky внутри контейнера ---
                let overlayTop = 0;
                const topEdge = contRect.top;
                const bottomEdge = contRect.bottom;
                // Важно: ищем sticky ЭЛЕМЕНТЫ внутри контейнера, которые перекрывают верх контейнера
                // (то есть их rect.top <= topEdge, а rect.bottom > topEdge)
                const nodes = Array.from(cont.querySelectorAll("*"));
                for (const node of nodes) {
                    if (!DomHelpers.isVisible(node))
                        continue;
                    const cs = getComputedStyle(node);
                    if (cs.position !== "sticky")
                        continue;
                    const r = node.getBoundingClientRect();
                    // должен пересекать верхнюю границу scrollport контейнера
                    if (r.bottom <= topEdge)
                        continue; // полностью выше
                    if (r.top > topEdge + 1)
                        continue; // не “прилип” к верху контейнера
                    // реальная перекрывающая высота в пределах контейнера
                    const clippedBottom = Math.min(r.bottom, bottomEdge);
                    const overlap = clippedBottom - topEdge;
                    if (overlap > overlayTop)
                        overlayTop = overlap;
                }
                if (overlayTop > 0) {
                    top -= Math.ceil(overlayTop);
                }
                // --- конец коррекции ---
            }
        }
        else if (typeof target === 'object' && !(target instanceof HTMLElement)) {
            if (typeof target.top === 'number')
                top = target.top;
            if (typeof target.left === 'number')
                left = target.left;
        }
        // === ОСТАЛЬНОЕ ===
        const axis = options.axis ?? 'y';
        const offset = options.offset ?? 0;
        if (axis.includes('y'))
            top += offset;
        if (axis.includes('x'))
            left += offset;
        const isWindow = resolvedContainer === window;
        const startTop = isWindow ? window.pageYOffset : resolvedContainer.scrollTop;
        const startLeft = isWindow ? window.pageXOffset : resolvedContainer.scrollLeft;
        const deltaTop = top - startTop;
        const deltaLeft = left - startLeft;
        const duration = options.duration ?? 400;
        options.onStart?.();
        if (duration <= 0) {
            const container = isWindow ? document.documentElement : resolvedContainer;
            // Сохраняем текущее значение
            const prevBehavior = container.style.scrollBehavior;
            // Временно выключаем плавность
            container.style.scrollBehavior = 'auto';
            if (axis.includes('y')) {
                if (isWindow)
                    window.scrollTo({ top, behavior: 'auto' });
                else
                    container.scrollTop = top;
            }
            if (axis.includes('x')) {
                if (isWindow)
                    window.scrollTo({ left, behavior: 'auto' });
                else
                    container.scrollLeft = left;
            }
            // Возвращаем исходное значение
            container.style.scrollBehavior = prevBehavior;
            options.onDone?.();
            return { cancel: () => { } };
        }
        let startTime = null;
        let animationId = null;
        function animateScroll(ts) {
            if (cancelled) {
                options.onCancel?.();
                return;
            }
            if (startTime == null)
                startTime = ts;
            let progress = Math.min((ts - startTime) / duration, 1);
            progress = progress < 0.5
                ? 2 * progress * progress
                : -1 + (4 - 2 * progress) * progress;
            const currTop = startTop + deltaTop * progress;
            const currLeft = startLeft + deltaLeft * progress;
            if (axis.includes('y')) {
                isWindow
                    ? window.scrollTo({ top: currTop, left: window.pageXOffset })
                    : resolvedContainer.scrollTop = currTop;
            }
            if (axis.includes('x')) {
                isWindow
                    ? window.scrollTo({ left: currLeft, top: window.pageYOffset })
                    : resolvedContainer.scrollLeft = currLeft;
            }
            if (progress < 1) {
                animationId = requestAnimationFrame(animateScroll);
            }
            else {
                if (axis.includes('y'))
                    isWindow ? window.scrollTo({ top }) : resolvedContainer.scrollTop = top;
                if (axis.includes('x'))
                    isWindow ? window.scrollTo({ left }) : resolvedContainer.scrollLeft = left;
                options.onDone?.();
            }
        }
        animationId = requestAnimationFrame(animateScroll);
        return {
            cancel: () => {
                cancelled = true;
                if (animationId)
                    cancelAnimationFrame(animationId);
                options.onCancel?.();
            }
        };
    }
}
// Активные анимации для каждого элемента
DomHelpers._slideAnimations = new WeakMap();

export { DomHelpers };
